// src/modules/app-views/goodsReceipt/pages/Document/tabs/RecordTab/ItemsCard.jsx
// *******************************************************************************
// GOODS RECEIPT ITEMS CARD
// *******************************************************************************
// Displays line items of the goods receipt in a table.
// Allows adding, editing, and removing items via ItemModal.
// Rendered inside a Collapse wrapper by RecordTab/index.jsx.
// Exposes handleAddClick via onActionsChange for Collapse extra button.
// Reuses shared item columns from invoices/pages/Init/components/tableColumns/items.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useMemo, useEffect } from 'react';
import { Button, Space, Typography, Popconfirm, Empty, Table } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import {
  itemLineNumberColumn,
  itemDescriptionColumn,
  itemSkuColumn,
  itemNcmColumn,
  itemQuantityColumn,
  itemUnitPriceColumn,
  itemTotalColumn,
} from 'modules/app-views/invoices/pages/Init/components/tableColumns/items';
import ItemModal from '../../modals/ItemModal';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ItemsCard = ({
  items = [],
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  viewMode,
  data,
  onActionsChange,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { form, submitForm } = useSimpleFormContext() || {};
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';
  const isConfirmed = data?.status === 'confirmed';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Expose add handler to parent for Collapse extra button
  useEffect(() => {
    onActionsChange?.({
      handleAddClick: () => {
        // Block if no issuer selected
        // Check form value (live) then fallback to saved data
        const formIssuer = form?.getFieldValue('issuer');
        const issuerId = formIssuer?.entityId || data?.issuer?.entityId || data?.issuerEntity?._id;
        if (!issuerId) {
          appService.message({ flag: 'w', text: 'select_issuer_first' });
          return;
        }
        setEditingItem(null);
        setEditingIndex(null);
        setShowItemModal(true);
      },
    });
  }, [onActionsChange]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleEditClick = (item, index) => {
    setEditingItem(item);
    setEditingIndex(index);
    setShowItemModal(true);
  };

  const handleModalConfirm = itemData => {
    const quantity = itemData.quantity || 1;
    const unitValue = itemData.unitValue || 0;
    const totalValue =
      itemData.totalValue ?? financialService.roundCurrency(quantity * unitValue);

    const processedItem = {
      ...itemData,
      quantity,
      unitValue,
      totalValue,
    };

    if (editingIndex !== null) {
      onUpdateItem(editingIndex, processedItem);
    } else {
      onAddItem(processedItem);
    }
    setShowItemModal(false);
    setEditingItem(null);
    setEditingIndex(null);

    // Auto-save silently after item confirm (items state updates async via setState)
    setTimeout(() => submitForm?.(), 100);
  };

  const handleModalClose = () => {
    setShowItemModal(false);
    setEditingItem(null);
    setEditingIndex(null);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      // Shared item columns from invoices (with fallbacks for goodsReceipt field names)
      // size: 'middle' for Document view (14px regular, 12px tax) vs 'small' default (11px/10px) in Init subtables
      itemLineNumberColumn({ size: 'middle' }),
      itemSkuColumn({ title: translateX('code'), size: 'middle' }),
      itemDescriptionColumn({ size: 'middle' }),
      itemNcmColumn({ className: undefined, size: 'middle' }),
      {
        title: translateX('un'),
        dataIndex: 'purchaseUnitUid',
        width: 80,
        align: 'center',
        render: uid => resolveUnitName(storedParameters, uid),
      },
      itemQuantityColumn({ size: 'middle' }),
      itemUnitPriceColumn({ size: 'middle' }),
      itemTotalColumn({ size: 'middle' }),

      // Actions column (specific to Document ItemsCard)
      {
        title: translateX('actions'),
        width: 90,
        align: 'center',
        render: (_, record, index) => (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record, index)}
              disabled={isLocked || isConfirmed}
            />
            <Popconfirm
              title={translateX('remove_item_confirm')}
              onConfirm={() => onRemoveItem(index)}
              okText={translateX('yes')}
              cancelText={translateX('no')}
              disabled={isLocked || isConfirmed}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={isLocked || isConfirmed}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [isLocked, isConfirmed, onRemoveItem, storedParameters],
  );

  const itemsTotal = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.totalValue || 0), 0);
  }, [items]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={translateX('no_items_added')} />
      ) : (
        <SimpleTable
          columns={columns}
          dataSource={items}
          rowKey={getRowKey}
          pagination={false}
          size="small"
          scroll={{ x: 700 }}
          onRow={(record, index) => ({
            onDoubleClick: () => !isLocked && !isConfirmed && handleEditClick(record, index),
          })}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7} align="right">
                <Text strong>{translateX('items_subtotal')}:</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong style={{ fontSize: 14 }}>
                  {financialService.formatCurrency(itemsTotal)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
            </Table.Summary.Row>
          )}
        />
      )}

      {/* Item Modal */}
      <ItemModal
        show={showItemModal}
        setShow={setShowItemModal}
        onConfirm={handleModalConfirm}
        onClose={handleModalClose}
        editingItem={editingItem}
        issuerId={form?.getFieldValue('issuer')?.entityId || data?.issuer?.entityId || data?.issuerEntity?._id}
        documentType={data?.documentType}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ItemsCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Get unique row key for table item
 */
function getRowKey(record) {
  const itemNumber = record.itemNumber ?? record._id ?? Math.random();
  return `${itemNumber}-${record.linkedProductId || record._id || record.key}`;
}

/**
 * Resolve purchaseUnitUid to unit name from parameters.
 */
function resolveUnitName(storedParameters, uid) {
  if (!uid) return '—';
  const units = storedParameters?.modules?.products?.units;
  if (!Array.isArray(units)) return uid;
  const found = units.find(u => u.uid === uid);
  return found?.name || '—';
}
